import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { AiTestJobOption } from './types';
import { listTesterJobs } from './api';

interface JobPickerProps {
  projectId: number | '';
  market: string;
  value: AiTestJobOption | null;
  onChange: (option: AiTestJobOption | null) => void;
  disabled?: boolean;
}

function sourceLabel(source: string): string {
  if (source === 'ai') return 'AI';
  if (source === 'blueprint') return 'Platform';
  if (source === 'user') return 'User';
  return source;
}

const JobPicker: React.FC<JobPickerProps> = ({
  projectId,
  market,
  value,
  onChange,
  disabled,
}) => {
  const [options, setOptions] = useState<AiTestJobOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (!projectId || !market) {
      setOptions([]);
      onChangeRef.current(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await listTesterJobs(projectId, market);
        if (cancelled) return;
        setOptions(rows);
        const current = valueRef.current;
        const stillValid = current && rows.some((row) => row.text === current.text);
        if (!stillValid) {
          onChangeRef.current(rows[0] || null);
        }
      } catch (err) {
        if (!cancelled) {
          setOptions([]);
          onChangeRef.current(null);
          setError(err instanceof Error ? err.message : 'Could not load jobs');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [projectId, market]);

  const ready = Boolean(projectId && market);

  return (
    <div>
      <label className="block text-[12px] font-medium text-[#626262] mb-1">
        Job to be done
      </label>
      {!ready && (
        <p className="rounded-md border border-[#D1D1D1] px-3 py-2 text-sm text-[#8A8A8A]">
          {!projectId ? 'Select a project first' : 'Select a source of truth first'}
        </p>
      )}
      {ready && (
        <div
          role="listbox"
          aria-label="Job to be done"
          className="w-full rounded-md border border-[#D1D1D1] bg-white"
        >
          {options.map((row, index) => {
            const selected = value?.text === row.text;
            return (
              <button
                key={`${row.source}-${index}-${row.text}`}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={disabled || loading}
                onClick={() => onChange(row)}
                className={`w-full text-left px-3 py-2.5 text-sm whitespace-normal break-words ${
                  index > 0 ? 'border-t border-[#E8E8E8]' : ''
                } ${
                  selected
                    ? 'bg-[#E8F4FB] text-[#0C0B0B]'
                    : 'text-[#0C0B0B] hover:bg-[#F7F7F7]'
                } disabled:opacity-50`}
              >
                <span className="mr-1.5 text-[11px] font-medium text-[#8A8A8A]">
                  {sourceLabel(row.source)}
                </span>
                {row.text}
              </button>
            );
          })}
        </div>
      )}
      {loading && (
        <div className="mt-1 flex items-center gap-2 text-[12px] text-[#8A8A8A]">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading jobs…
        </div>
      )}
      {error && <p className="mt-1 text-[12px] text-red-600">{error}</p>}
      {!loading && ready && options.length === 0 && !error && (
        <p className="mt-1 text-[12px] text-[#8A8A8A]">
          No jobs found for this project and market yet.
        </p>
      )}
    </div>
  );
};

export default JobPicker;
